/**
 * Form Utilities
 * Helper functions for dynamic form processing and validation
 */

/**
 * Extract required field names from form configuration
 * @param {Object} config - Form configuration object
 * @returns {Array} Array of required field names
 */
export const extractRequiredFields = (config) => {
  if (!config?.sections) return [];

  const requiredFields = [];

  config.sections.forEach(section => {
    if (section.fields) {
      section.fields.forEach(field => {
        if (field.required && field.name) {
          requiredFields.push(field.name);
        }
      });
    }
  });

  return requiredFields;
};

/**
 * Get all field names from form configuration
 * @param {Object} config - Form configuration object
 * @returns {Array} Array of all field names
 */
export const extractAllFieldNames = (config) => {
  if (!config?.sections) return [];

  const allFields = [];

  config.sections.forEach(section => {
    if (section.fields) {
      section.fields.forEach(field => {
        if (field.name) {
          allFields.push(field.name);
        }
      });
    }
  });

  return allFields;
};

/**
 * Validate form configuration structure
 * @param {Object} config - Form configuration object
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export const validateFormConfig = (config) => {
  const errors = [];

  if (!config) {
    errors.push('Configuration is required');
    return { isValid: false, errors };
  }

  if (!config.metadata) {
    errors.push('Configuration metadata is required');
  }

  if (!config.sections || !Array.isArray(config.sections)) {
    errors.push('Configuration sections must be an array');
  } else {
    config.sections.forEach((section, index) => {
      if (!section.id) {
        errors.push(`Section ${index + 1} is missing an id`);
      }
      if (!section.fields || !Array.isArray(section.fields)) {
        errors.push(`Section ${section.id || index + 1} fields must be an array`);
      } else {
        section.fields.forEach((field, fieldIndex) => {
          if (!field.name) {
            errors.push(`Field ${fieldIndex + 1} in section ${section.id || index + 1} is missing a name`);
          }
          if (!field.type) {
            errors.push(`Field ${field.name || fieldIndex + 1} in section ${section.id || index + 1} is missing a type`);
          }
        });
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get field configuration by name
 * @param {Object} config - Form configuration object
 * @param {string} fieldName - Name of the field to find
 * @returns {Object|null} Field configuration or null if not found
 */
export const getFieldConfig = (config, fieldName) => {
  if (!config?.sections || !fieldName) return null;

  for (const section of config.sections) {
    if (section.fields) {
      const field = section.fields.find(f => f.name === fieldName);
      if (field) return field;
    }
  }

  return null;
};

/**
 * Calculate form completion percentage
 * @param {Object} formData - Current form data
 * @param {Array} requiredFields - Array of required field names
 * @returns {number} Completion percentage (0-100)
 */
export const calculateFormProgress = (formData, requiredFields) => {
  if (!requiredFields || requiredFields.length === 0) return 100;

  const completedFields = requiredFields.filter(fieldName => {
    const value = formData[fieldName];
    return value && value.toString().trim() !== '';
  });

  return Math.round((completedFields.length / requiredFields.length) * 100);
};

/**
 * Get form section by id
 * @param {Object} config - Form configuration object
 * @param {string} sectionId - ID of the section to find
 * @returns {Object|null} Section configuration or null if not found
 */
export const getSectionConfig = (config, sectionId) => {
  if (!config?.sections || !sectionId) return null;

  return config.sections.find(section => section.id === sectionId) || null;
};

/**
 * Check if form is complete (all required fields filled)
 * @param {Object} formData - Current form data
 * @param {Array} requiredFields - Array of required field names
 * @returns {boolean} True if all required fields are completed
 */
export const isFormComplete = (formData, requiredFields) => {
  if (!requiredFields || requiredFields.length === 0) return true;

  return requiredFields.every(fieldName => {
    const value = formData[fieldName];
    return value && value.toString().trim() !== '';
  });
};